// Brengen en halen per weekdag (0022, verzoek oprichter 2026-09-24): "maandag brengt iemand anders
// dan op donderdag", en "voor die dag aanpassen als de een niet kan". Pure functies zonder
// runtime-afhankelijkheden: de server rekent er de agenda, het bord, de ritten en de vertrekbanners
// mee uit; de ouderapp gebruikt dezelfde weekdagen en dezelfde volgorde in de editor.
//
// Wie rijdt er bij één keer? (resolveRide)
//   1. override  aanpassing van alleen deze keer (event_override.payload.roles);
//   2. weekday   de regel voor de weekdag van de OORSPRONKELIJKE datum van de keer;
//   3. series    de standaard van de serie (event_role.member_id);
//   4. none      niemand ("Wie rijdt?").
// Een verplaatste keer (maandag naar dinsdag geschoven) houdt dus de rijder van maandag, tenzij
// die keer zelf is aangepast: wie op maandag brengt, weet dat hij deze week op dinsdag rijdt.
import { weekdayIndex, weekdayOf } from './localDate.js';
import { parseRRule } from './rrule.js';
export const RIDE_ROLES = ['dropoff', 'pickup'];
/** ISO-weekdag (1 = maandag ... 7 = zondag) van een kalenderdatum. */
export function isoWeekday(date) {
    return weekdayIndex(weekdayOf(date)) + 1;
}
/**
 * Normale rit op een datum (zonder aanpassing van deze keer): weekdagregel, anders de standaard
 * van de serie. null = geen rit (de rol bestaat niet of is op deze weekdag niet nodig).
 */
export function usualRide(role, defaults, rules, originalDate) {
    const base = defaults.find((d) => d.role === role);
    if (!base)
        return null;
    const wd = isoWeekday(originalDate);
    const rule = rules.find((r) => r.role === role && r.weekday === wd);
    if (rule) {
        if (!rule.needed)
            return null;
        return { role, memberId: rule.memberId, offsetMinutes: base.offsetMinutes, source: 'weekday' };
    }
    return {
        role,
        memberId: base.memberId,
        offsetMinutes: base.offsetMinutes,
        source: base.memberId === null ? 'none' : 'series',
    };
}
/** Wie rijdt er bij één keer (zie de volgorde bovenaan); null = geen rit voor deze rol. */
export function resolveRide(role, defaults, rules, overrides, originalDate) {
    const usual = usualRide(role, defaults, rules, originalDate);
    const o = overrides ? overrides[role] : undefined;
    if (o === null)
        return null;
    if (o !== undefined) {
        return {
            role,
            memberId: o.member_id,
            offsetMinutes: o.offset_minutes ?? usual?.offsetMinutes ?? 0,
            source: 'override',
            ...(usual ? { usual: { memberId: usual.memberId } } : {}),
        };
    }
    if (!usual)
        return null;
    return { ...usual, usual: { memberId: usual.memberId } };
}
/** Beide rollen van één keer, brengen eerst. */
export function resolveRides(defaults, rules, overrides, originalDate) {
    const out = [];
    for (const role of RIDE_ROLES) {
        const r = resolveRide(role, defaults, rules, overrides, originalDate);
        if (r)
            out.push(r);
    }
    return out;
}
/**
 * Weekdagen waarop een serie een eigen rijder per dag kan hebben (ISO 1..7, oplopend): bij een
 * wekelijkse herhaling de gekozen dagen (BYDAY zonder rangtelwoord, anders de dag van de start),
 * bij een dagelijkse herhaling BYDAY of alle zeven dagen. Eenmalig, maandelijks, jaarlijks of een
 * onleesbare regel: geen (de editor toont dan de eenvoudige vorm).
 */
export function rideWeekdays(rrule, startDate) {
    if (!rrule)
        return [];
    let rule;
    try {
        rule = parseRRule(rrule).rule;
    }
    catch {
        return [];
    }
    if (rule.freq !== 'WEEKLY' && rule.freq !== 'DAILY')
        return [];
    const days = (rule.byDay ?? []).filter((d) => d.ordinal === undefined);
    if (days.length > 0) {
        return [...new Set(days.map((d) => weekdayIndex(d.weekday) + 1))].sort((a, b) => a - b);
    }
    if (rule.freq === 'DAILY')
        return [1, 2, 3, 4, 5, 6, 7];
    return [isoWeekday(startDate)];
}
/**
 * Minimale regels voor een keuze per weekdag: de standaard van de serie wordt de meest gekozen
 * rijder (bij gelijke stand de vroegste weekdag), en alleen de dagen die daarvan afwijken krijgen
 * een regel. `choice`: lid-id, 'open' (nog niet bekend) of 'none' (niet nodig). Alle dagen
 * 'none' = de rol is niet nodig (default null, geen regels).
 */
export function compactWeekdayChoices(role, choices) {
    const sorted = [...choices].sort((a, b) => a.weekday - b.weekday);
    const needed = sorted.filter((c) => c.choice !== 'none');
    if (needed.length === 0)
        return { default: null, rules: [] };
    const counts = new Map();
    for (const c of needed)
        counts.set(c.choice, (counts.get(c.choice) ?? 0) + 1);
    let best = needed[0]?.choice ?? 'open';
    for (const c of needed) {
        if ((counts.get(c.choice) ?? 0) > (counts.get(best) ?? 0))
            best = c.choice;
    }
    const memberOf = (choice) => (choice === 'open' ? null : choice);
    const rules = [];
    for (const c of sorted) {
        if (c.choice === best)
            continue;
        rules.push(c.choice === 'none'
            ? { role, weekday: c.weekday, memberId: null, needed: false }
            : { role, weekday: c.weekday, memberId: memberOf(c.choice), needed: true });
    }
    return { default: { memberId: memberOf(best) }, rules };
}
//# sourceMappingURL=rides.js.map